import React from "react";
import { BookOpen, Target, RotateCcw } from "lucide-react";
import type { SowFields } from "../../../../../utils/api/endpoints/sow";

interface IntroObjectivesEditorProps {
  fields: SowFields;
  setField: <K extends keyof SowFields>(key: K, value: SowFields[K]) => void;
  clientName: string;
}

export const IntroObjectivesEditor: React.FC<IntroObjectivesEditorProps> = ({
  fields,
  setField,
  clientName
}) => {
  const clientDisplay = clientName && clientName.trim() ? clientName.trim() : "Client";

  const defaultIntro = `Capture The Bug (CTB) will conduct a penetration test for ${clientDisplay}, covering the agreed scope, testing approach, deliverables, responsibilities, and commercial terms. Appendix A - Rules of Engagement forms part of this Statement of Work and defines the operational conditions for testing.

The assessment is performed remotely through the CTB PTaaS platform against the environment(s) agreed with ${clientDisplay}, using the testing approach set out in the Engagement Structure.

The engagement will identify and validate security weaknesses across authorised targets and provide prioritised remediation guidance. Retesting is included as set out in this document.`;

  const defaultObjective = `The objective is to identify and validate material vulnerabilities within ${clientDisplay}'s authorised targets as defined in this Statement of Work. Particular attention will be given to authentication and session management, authorisation and access-control enforcement, the integrity of core business workflows, and the security of any privileged or sensitive functionality within scope. CTB will prioritise these attack classes during manual testing where they are relevant to the identified attack surface, while maintaining comprehensive coverage of the agreed scope.

The engagement also provides ${clientDisplay} with an independent assessment of its externally exposed attack surface to support internal risk-management activities. This assessment does not constitute a formal compliance audit, certification, or attestation, and no such outcome is offered or implied.`;

  return (
    <div className="space-y-6">
      <div className="border-b border-slate-200 pb-4">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <BookOpen size={16} className="text-ctb-blue" />
          Section 1 & 2: Introduction & Engagement Objectives
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Customize the introductory legal narrative and the technical engagement objectives. Every sentence is fully editable and renders directly in the PDF.
        </p>
      </div>

      {/* Introduction */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
            <BookOpen size={13} className="text-slate-500" />
            1. Introduction Narrative
          </label>
          <button
            type="button"
            onClick={() => setField("introductionText", defaultIntro)}
            className="text-[11px] text-ctb-blue hover:underline flex items-center gap-1 cursor-pointer"
          >
            <RotateCcw size={11} />
            Reset to default
          </button>
        </div>
        <textarea
          rows={5}
          value={fields.introductionText ?? ""}
          onChange={(e) => setField("introductionText", e.target.value)}
          className="w-full text-xs text-slate-800 border border-slate-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue leading-relaxed font-sans"
          placeholder="Enter introduction text..."
        />
        <span className="text-[10px] text-slate-400">
          Identifies the contracting parties, legal relationship, and engagement intent.
        </span>
      </div>

      {/* Objectives */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
            <Target size={13} className="text-slate-500" />
            2. Primary Technical Objectives
          </label>
          <button
            type="button"
            onClick={() => setField("objectiveText", defaultObjective)}
            className="text-[11px] text-ctb-blue hover:underline flex items-center gap-1 cursor-pointer"
          >
            <RotateCcw size={11} />
            Reset to default
          </button>
        </div>
        <textarea
          rows={5}
          value={fields.objectiveText ?? ""}
          onChange={(e) => setField("objectiveText", e.target.value)}
          className="w-full text-xs text-slate-800 border border-slate-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue leading-relaxed font-sans"
          placeholder="Enter engagement objectives..."
        />
        <span className="text-[10px] text-slate-400">
          Summarizes the technical goals, risk identification criteria, and remediation deliverables.
        </span>
      </div>
    </div>
  );
};
